import { Observable } from 'rxjs';

/**
 * Source of cache events.
 */
export type CacheSource = 'websocket' | 'serviceWorker' | 'api' | 'manual';

/**
 * Standard operation types for cache status updates.
 * Using this enum ensures consistent operation naming across the system.
 */
export enum CacheOperationType {
  // Area operations
  AREA_LOAD = 'AREA_LOAD',
  AREA_LOADING_COMPLETE = 'AREA_LOADING_COMPLETE',

  // File operations
  FILE_DOWNLOAD = 'FILE_DOWNLOAD',

  // WebSocket operations
  WEBSOCKET_UPDATE = 'WEBSOCKET_UPDATE',

  // Invalidate operations
  INVALIDATE = 'INVALIDATE'
}

/**
 * Configuration for a cache store.
 */
export interface CacheStoreConfig {
  storeName: string;
  keyPath: string;
  topic: string;
  projectKeyPath?: string;
  indices?: { name: string; keyPath: string; options?: IDBIndexParameters }[];
  version?: string;
}

/**
 * Generic cache item.
 */
export interface CacheItem {
  [key: string]: unknown;
  timestamp: number;
}

/**
 * Cache event types.
 */
export interface CacheEvent<T> {
  type: 'update' | 'invalidate' | 'clear';
  storeId: string;
  itemId?: string | number;
  projectId?: string;
  item?: T;
  timestamp: number;
  source: CacheSource;
}

/**
 * WebSocket message with enhanced properties.
 */
export interface CacheWebSocketMessage<T> {
  topic: string;
  data: T;
  action?: string;
  projectId?: string;
}

/**
 * Filter for cache events.
 */
export interface EventFilter {
  storeId?: string;
  projectId?: string;
  type?: 'update' | 'invalidate' | 'clear';
}

/**
 * Status of cache operations.
 */
export interface CacheStatus {
  isLoading: boolean;
  progress: number;
  storeId?: string;
  itemId?: string;
  projectId?: string;
  areaId?: string;
  operation?: CacheOperationType | string;
  error?: boolean;
}

/**
 * Filter for status updates.
 */
export interface StatusFilter {
  storeId?: string;
  projectId?: string;
  areaId?: string;
  itemId?: string | number;
  operation?: CacheOperationType | string;
}

/**
 * Core interface for cache stores.
 */
export interface CacheStore<T> {
  get(id: string | number): Observable<T | null>;
  getBySlug(slug: string, projectId: string): Observable<T | null>;
  getByIndex<U = T>(indexName: string, key: string | number): Observable<U[]>;
  set(item: T): Observable<T>;
  remove(id: string | number): Observable<boolean>;
  clear(projectId?: string): Observable<boolean>;
  needsRefresh(itemId: string | number, projectId: string): Promise<boolean>;
}

/**
 * Handler for WebSocket messages.
 */
export interface WebSocketMessageHandler<T> {
  canHandle(message: CacheWebSocketMessage<T>): boolean;
  handle(message: CacheWebSocketMessage<T>): Observable<T | boolean | undefined>;
}

/**
 * Policy for cache eviction.
 */
export interface CachePolicy {
  shouldEvict(item: CacheItem): boolean;
  prioritize(items: CacheItem[]): CacheItem[];
}

/**
 * Lightweight change notification containing only ID and operation type.
 */
export interface ChangedItemNotification {
  id: string | number;
  operation: 'update' | 'delete';
  timestamp: number;
}

/**
 * Response format for lightweight changed items query.
 */
export interface ChangedItemNotificationsResponse {
  items: ChangedItemNotification[];
  timestamp: number;
}

export interface BackgroundFetchJob {
  id: string | number;
  operation: 'update' | 'delete';
  timestamp: number;
  storeName: string;
  projectId: string;
  userId?: string;
  priority: number;
}
